import CloseIcon from '@mui/icons-material/Close';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import { ClickAwayListener, Grow, Popper } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NewsletterSubscribeForm } from 'src/components/common/NewsletterSubscribeForm';

const DISMISSED_KEY = 'dealsNewsletterCardDismissed';
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000; // one week

type DismissState = { dismissedForever?: boolean; snoozedAt?: number };

function readState(): DismissState {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    // This key used to hold the literal string 'true' for a permanent dismissal;
    // honour it so anyone who already closed the card is not nagged again.
    if (parsed === true) return { dismissedForever: true };
    return parsed && typeof parsed === 'object' ? (parsed as DismissState) : {};
  } catch {
    return {};
  }
}

function writeState(state: DismissState) {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(state));
  } catch {
    /* localStorage unavailable (private mode / quota) — fail silently */
  }
}

function shouldAutoOpen(): boolean {
  const state = readState();
  if (state.dismissedForever) return false;
  if (state.snoozedAt && Date.now() - state.snoozedAt < SNOOZE_MS) return false;
  return true;
}

/**
 * Deals signup, opened from a mail button floating in the bottom-right. It opens on
 * its own the first time; the X suppresses that for good, anything else snoozes it.
 */
export const DealsNewsletterCard = () => {
  const { t } = useTranslation();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (shouldAutoOpen()) {
      setOpen(true);
    }
  }, []);

  // Everything but the X is a soft dismiss: the card stays out of the way for a
  // week rather than never opening on its own again.
  const closeForNow = () => {
    setOpen(false);
    writeState({ ...readState(), snoozedAt: Date.now() });
  };

  const closeForGood = () => {
    setOpen(false);
    writeState({ ...readState(), dismissedForever: true });
  };

  // Popper has no built-in dismiss affordances, so Escape is wired up by hand.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeForNow();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open]);

  return (
    <>
      {/* The gradient lives on ::before so hover can fade it: gradient stops are custom
          properties inside background-image, which CSS cannot transition. */}
      <button
        type='button'
        ref={triggerRef}
        onClick={() => (open ? closeForNow() : setOpen(true))}
        aria-label={t('deals.newsletter_card_open')}
        data-attr='deals-newsletter-card-open'
        className='fixed bottom-6 right-6 z-40 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-offWhite shadow-[0_6px_20px_rgba(48,50,220,0.3)] ring-1 ring-white/20 transition-transform before:absolute before:inset-0 before:rounded-full before:bg-gradient-to-br before:from-primary before:to-primary-dark before:transition-opacity before:duration-200 active:scale-95 hover:before:opacity-85'
      >
        <MailOutlineIcon className='relative' style={{ fontSize: '20px' }} />
      </button>

      <ClickAwayListener
        onClickAway={(e) => {
          // The trigger owns its own toggle; without this it would reopen and
          // immediately close again on the same click.
          if (triggerRef.current?.contains(e.target as Node)) return;
          if (open) closeForNow();
        }}
      >
        <Popper
          open={open}
          anchorEl={triggerRef.current}
          placement='top-end'
          transition
          modifiers={[
            { name: 'offset', options: { offset: [0, 8] } },
            { name: 'preventOverflow', options: { padding: 16 } },
          ]}
          sx={{ zIndex: 41 }}
        >
          {({ TransitionProps }) => (
            <Grow {...TransitionProps} timeout={200} style={{ transformOrigin: 'bottom right' }}>
              <div className='relative flex w-[min(340px,calc(100vw-2rem))] flex-col gap-3 rounded-2xl bg-gradient-to-br from-primary to-primary-dark p-5 pr-6 shadow-[0_14px_44px_rgba(48,50,220,0.4)] ring-1 ring-white/20'>
                <button
                  type='button'
                  onClick={closeForGood}
                  aria-label={t('common.close')}
                  data-attr='deals-newsletter-card-close'
                  className='absolute right-2 top-2 text-white/60 transition-colors hover:text-white active:scale-95'
                >
                  <CloseIcon style={{ fontSize: '20px' }} />
                </button>

                <div className='flex flex-col gap-1'>
                  <h3 className='font-primary text-base font-bold text-offWhite'>
                    {t('deals.newsletter_card_title')}
                  </h3>
                  <p className='font-secondary text-sm text-blueGray'>
                    {t('deals.newsletter_card_body')}
                  </p>
                </div>

                <NewsletterSubscribeForm variant='card' />
              </div>
            </Grow>
          )}
        </Popper>
      </ClickAwayListener>
    </>
  );
};
